import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from 'react';
import { type Command, CommandRegistry } from './command-registry.js';
import { HotkeyDispatcher } from './dispatcher.js';
import { type Scope, ScopeStack } from './scope-stack.js';

type HotkeysValue = { registry: CommandRegistry; scopes: ScopeStack };

const HotkeysContext = createContext<HotkeysValue | null>(null);

export const HotkeysProvider = ({
  children,
  value,
}: {
  children: ReactNode;
  value?: HotkeysValue;
}) => {
  const ctx = useMemo(
    () => value ?? { registry: new CommandRegistry(), scopes: new ScopeStack() },
    [value],
  );

  useEffect(() => {
    const dispatcher = new HotkeyDispatcher(ctx.registry, ctx.scopes);
    const onKeyDown = (event: KeyboardEvent): void => {
      dispatcher.handle(event as unknown as Parameters<typeof dispatcher.handle>[0]);
    };
    globalThis.addEventListener('keydown', onKeyDown);
    return () => globalThis.removeEventListener('keydown', onKeyDown);
  }, [ctx]);

  return <HotkeysContext.Provider value={ctx}>{children}</HotkeysContext.Provider>;
};

export const useHotkeys = (): HotkeysValue => {
  const ctx = useContext(HotkeysContext);
  if (ctx === null) throw new Error('useHotkeys outside HotkeysProvider');
  return ctx;
};

/** Registra comandos mientras el componente está montado. */
export const useCommands = (commands: readonly Command[]): void => {
  const { registry } = useHotkeys();
  useEffect(() => registry.registerAll(commands), [registry, commands]);
};

/**
 * Apila un scope mientras el componente está abierto: panel, popover o editor.
 *
 * `active` existe por el editor: montado pero sin foco no debe quedarse el scope, o el panel
 * que lo contiene nunca volvería a recibir `Esc`.
 */
export const useScope = (scope: Scope, active = true): void => {
  const { scopes } = useHotkeys();
  useEffect(() => (active ? scopes.push(scope) : undefined), [scopes, scope, active]);
};

/**
 * Cadena de scopes activa, reactiva, para que la paleta muestre lo alcanzable.
 * `useSyncExternalStore` y no un efecto: el panel se apila en su propio efecto y un
 * suscriptor tardío se perdería ese primer cambio.
 */
export const useScopeChain = (): Scope[] => {
  const { scopes } = useHotkeys();
  return useSyncExternalStore(
    (listener) => scopes.subscribe(listener),
    () => scopes.chain(),
  );
};
